/**
 * Seamless looping marquee for homepage carousels.
 *
 * CSS `translateX(-50%)` + fixed duration is unreliable on iOS (especially with
 * `width: max-content`) and feels glacial when the track is long. This drives
 * motion with rAF + measured pixel distance at a constant px/sec — same feel
 * on iPhone and desktop.
 */

import React from "react";

/**
 * Below this unique-item count, prefer ticker (one copy). Loop mode duplicates
 * the track for a seamless wrap, which makes the same post/product appear
 * multiple times on screen when the strip is short (e.g. 2–3 custom products).
 */
export const MARQUEE_LOOP_MIN_ITEMS = 5;

/** True when a short strip should scroll as a single ticker, not a duplicated loop. */
export function shouldMarqueeTicker(itemCount: number): boolean {
  return itemCount > 0 && itemCount < MARQUEE_LOOP_MIN_ITEMS;
}

type Props = {
  children: React.ReactNode;
  /** When true, freeze marquee motion (mobile tap-to-focus). */
  paused?: boolean;
  /** Horizontal speed. Desktop default 60; slower on tablet/phone. */
  pxPerSec?: number;
  /** Extra class on the moving track (appended after defaults unless `trackClassName`). */
  className?: string;
  /** Override outer shell class (default: carousel marquee outer). */
  outerClassName?: string;
  /** Optional style on the outer shell (e.g. CSS vars for logo sizing). */
  outerStyle?: React.CSSProperties;
  /** Replace default track classes entirely (e.g. ribbon banners). */
  trackClassName?: string;
  /**
   * loop: caller duplicates children; wrap at half the track (carousels).
   * ticker: one copy enters from the right edge, exits left, then restarts.
   */
  mode?: "loop" | "ticker";
};

/** Pure default used when `pxPerSec` is omitted — desktop (≥1024) stays 60. */
export function defaultMarqueePxPerSec(widthPx: number): number {
  if (widthPx <= 480) return 36;
  if (widthPx <= 768) return 42;
  if (widthPx <= 1023) return 48;
  return 60;
}

export function TemplateMarqueeTrack({
  children,
  paused = false,
  pxPerSec,
  className,
  outerClassName = "mte-tpl-carl-marquee-outer",
  outerStyle,
  trackClassName,
  mode = "loop",
}: Props) {
  const outerRef = React.useRef<HTMLDivElement | null>(null);
  const trackRef = React.useRef<HTMLDivElement | null>(null);
  const offsetRef = React.useRef(0);
  const halfRef = React.useRef(0);
  /** Ticker: visible width of the clip, and width of one full right-to-left pass. */
  const viewportRef = React.useRef(0);
  const cycleRef = React.useRef(0);
  const rafRef = React.useRef(0);
  /** Visible in viewport (IntersectionObserver). */
  const inViewRef = React.useRef(true);
  /** Desktop hover pause — never sticky on touch. */
  const hoverPausedRef = React.useRef(false);
  const tapPausedRef = React.useRef(false);
  const lastTsRef = React.useRef(0);
  const speedRef = React.useRef(60);

  React.useEffect(() => {
    tapPausedRef.current = paused;
    if (paused) lastTsRef.current = 0;
  }, [paused]);

  const isRunning = () =>
    inViewRef.current && !hoverPausedRef.current && !tapPausedRef.current;

  React.useEffect(() => {
    if (pxPerSec != null) {
      speedRef.current = pxPerSec;
      return;
    }
    const width =
      typeof window !== "undefined" ? window.innerWidth : 1280;
    speedRef.current = defaultMarqueePxPerSec(width);
  }, [pxPerSec]);

  React.useLayoutEffect(() => {
    const el = trackRef.current;
    const outer = outerRef.current;
    if (!el || typeof window === "undefined") return;

    offsetRef.current = 0;
    halfRef.current = 0;
    viewportRef.current = 0;
    cycleRef.current = 0;

    const reduceMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    )?.matches;
    if (reduceMotion) {
      el.style.transform = "";
      el.style.visibility = "";
      return;
    }

    const measure = () => {
      if (mode === "ticker") {
        const viewport = outer?.clientWidth ?? 0;
        const content = el.scrollWidth;
        const prevViewport = viewportRef.current;
        const prevCycle = cycleRef.current;
        viewportRef.current = viewport;
        cycleRef.current = viewport + content;
        const contentDelta = Math.abs(content - (prevCycle - prevViewport));
        const viewportDelta = Math.abs(viewport - prevViewport);
        // Ignore subpixel font rounding. A real width change restarts at the
        // right edge so the line cannot pop to a new position mid-banner.
        if (
          prevViewport === 0 ||
          viewportDelta > 8 ||
          contentDelta > 8
        ) {
          offsetRef.current = 0;
        }
        if (viewport > 0) {
          // Place the left edge of the text exactly on the right clip edge.
          el.style.visibility = "visible";
          el.style.transform = `translate3d(${viewport - offsetRef.current}px, 0, 0)`;
        }
        return;
      }
      // Duplicated children: distance from the first card to the second copy.
      // scrollWidth / 2 includes track padding and one fewer gap, so the wrap pops.
      const kids = el.children;
      const copies =
        kids.length >= 2 && kids.length % 2 === 0 ? kids.length / 2 : 0;
      if (copies > 0) {
        const start = kids[0] as HTMLElement;
        const next = kids[copies] as HTMLElement;
        const loop = next.offsetLeft - start.offsetLeft;
        halfRef.current = loop > 1 ? loop : el.scrollWidth / 2;
      } else {
        halfRef.current = el.scrollWidth / 2;
      }
    };
    measure();
    // iOS often lays out images a frame late; remeasure a few times.
    const remMeasure = window.setTimeout(measure, 120);
    const remMeasure2 = window.setTimeout(measure, 600);

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            measure();
          })
        : null;
    ro?.observe(el);
    if (outer && outer !== el) ro?.observe(outer);

    const tick = (now: number) => {
      if (!isRunning()) {
        lastTsRef.current = now;
        rafRef.current = requestAnimationFrame(tick);
        return;
      }
      const last = lastTsRef.current || now;
      const dt = Math.min(0.064, (now - last) / 1000);
      lastTsRef.current = now;
      if (mode === "ticker") {
        const cycle = cycleRef.current;
        const viewport = viewportRef.current;
        // Travel viewport + text width, then wrap. Both ends sit fully outside
        // the clip, so the restart is a blank frame — no visible jump, and the
        // line is on screen only once.
        if (cycle > viewport && viewport > 0) {
          offsetRef.current = (offsetRef.current + speedRef.current * dt) % cycle;
          el.style.transform = `translate3d(${viewport - offsetRef.current}px, 0, 0)`;
        }
      } else {
        const half = halfRef.current;
        if (half > 1) {
          offsetRef.current = (offsetRef.current + speedRef.current * dt) % half;
          el.style.transform = `translate3d(${-offsetRef.current}px, 0, 0)`;
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    inViewRef.current = true;
    hoverPausedRef.current = false;
    lastTsRef.current = 0;
    rafRef.current = requestAnimationFrame(tick);

    // Pause off-screen — iOS throttles anyway; saves battery when scrolled away.
    const io =
      typeof IntersectionObserver !== "undefined"
        ? new IntersectionObserver(
            (entries) => {
              const hit = entries[0];
              inViewRef.current = Boolean(hit?.isIntersecting);
              if (hit?.isIntersecting) lastTsRef.current = 0;
            },
            { root: null, threshold: 0.01 },
          )
        : null;
    if (outer && io) io.observe(outer);

    // Match prior CSS: pause on hover only for fine pointers (not iOS sticky :hover).
    const canHoverPause = window.matchMedia?.(
      "(hover: hover) and (pointer: fine)",
    )?.matches;
    const onEnter = () => {
      hoverPausedRef.current = true;
    };
    const onLeave = () => {
      hoverPausedRef.current = false;
      lastTsRef.current = 0;
    };
    if (outer && canHoverPause) {
      outer.addEventListener("pointerenter", onEnter);
      outer.addEventListener("pointerleave", onLeave);
    }

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.clearTimeout(remMeasure);
      window.clearTimeout(remMeasure2);
      ro?.disconnect();
      io?.disconnect();
      if (outer && canHoverPause) {
        outer.removeEventListener("pointerenter", onEnter);
        outer.removeEventListener("pointerleave", onLeave);
      }
      el.style.transform = "";
      el.style.visibility = "";
    };
  }, [mode]);

  return (
    <div ref={outerRef} className={outerClassName} style={outerStyle}>
      <div
        ref={trackRef}
        style={mode === "ticker" ? { visibility: "hidden" } : undefined}
        className={[
          trackClassName ||
            "mte-tpl-carl-marquee-track mte-tpl-carl-marquee-track--js",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {children}
      </div>
    </div>
  );
}
